import { Link } from "react-router-dom";
import {
  ArrowRight,
  Landmark,
  ShieldCheck,
  LineChart,
  ClipboardCheck,
  Globe2,
  Award,
  FileText,
} from "lucide-react";

const SERVICES = [
  {
    icon: LineChart,
    title: "Budget Operations",
    blurb:
      "Formulation and execution support for federal funds exceeding $400M annually. Multi-year budget builds, mid-year reprogramming, and execution analytics.",
  },
  {
    icon: ShieldCheck,
    title: "Compliance & Audit",
    blurb:
      "Internal-controls assessments under OMB Circular A-123, audit liaison with IGs and external auditors, and ISO-aligned process documentation.",
  },
  {
    icon: ClipboardCheck,
    title: "Working Capital Fund (WCF) Management",
    blurb:
      "WCF formulation and execution support, environmental scans, customer-rate development, and full-cost recovery modeling for agency service organizations.",
  },
  {
    icon: Landmark,
    title: "Financial Analysis & Re-engineering",
    blurb:
      "Strategic process re-engineering, P&L reporting, Power BI dashboards, financial SOP development, and cost-reduction initiatives.",
  },
];

const PAST_PERFORMANCE = [
  {
    client: "U.S. Department of State",
    role: "Senior Financial Operations",
    detail:
      "Managed $200M+ in annual funds. Led an electronic-signature implementation that reduced administrative costs by 75%.",
  },
  {
    client: "U.S. Department of Labor",
    role: "Advisory · Office of the CFO",
    detail:
      "Advised the CFO on re-engineering the $400M Working Capital Fund budget-formulation process.",
  },
  {
    client: "HHS / ASPR",
    role: "Budget Support · WCF",
    detail:
      "Provided Working Capital Fund budget support; developed Power BI dashboards and financial standard operating procedures.",
  },
  {
    client: "City of Baltimore — Department of Parks & Recreation",
    role: "Business Management & Financial Advisory",
    detail:
      "Provided business-management and financial-advisory services to the Special Facilities section.",
  },
  {
    client: "State of Delaware — Mental Health Projects",
    role: "Grants Compliance & Performance Management",
    detail:
      "Provided grants-compliance oversight and performance management on ECOVID and SOR contracts.",
  },
  {
    client: "Jet.com (Walmart)",
    role: "Custom Financial Training",
    detail:
      "Developed and delivered customized financial-accounting training for HR leadership.",
  },
  {
    client: "U.S. Embassies — Global",
    role: "Financial Operations Lead",
    detail:
      "Financial-leadership postings in Panama, Algeria, Paraguay, and Côte d'Ivoire. Negotiated banking relationships and significantly reduced administrative costs.",
  },
];

const NAICS = [
  { code: "541611", desc: "Administrative Management & General Management Consulting" },
  { code: "523930", desc: "Financial Planning Services" },
  { code: "541213", desc: "Tax Preparation Services" },
  { code: "611430", desc: "Professional & Management Development Training" },
];

const DIFFERENTIATORS = [
  {
    icon: Globe2,
    title: "Global Field Experience",
    body: "Financial-leadership tours at U.S. Embassies in Panama, Algeria, Paraguay, and Côte d'Ivoire. Comfortable operating across time zones, currencies, and regulatory regimes.",
  },
  {
    icon: Award,
    title: "Elite Credentials",
    body: "MBA · Enrolled Agent (EA) · Project Management Professional (PMP). See the Team page for the full credentials list.",
  },
  {
    icon: ClipboardCheck,
    title: "Deep Federal SME",
    body: "Subject-matter expertise in federal budgeting, Working Capital Funds, OMB Circular A-123 internal controls, and data-visualization for executive decision support.",
  },
];

export default function GovernmentPage() {
  return (
    <div data-testid="page-government" className="bg-[#F9F8F6]">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-16">
        <div className="text-xs uppercase tracking-[0.3em] text-[#8E6A2B] mb-6">— Government Contracting</div>
        <div className="grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-[#0F292F] font-light tracking-tight leading-[1.05]">
              Federal & state<br />
              <span className="italic text-[#8E6A2B]">contracting</span> experience.
            </h1>
            <p className="mt-8 text-lg md:text-xl text-[#52525B] max-w-2xl leading-relaxed">
              H. Elliot Associates, LLC has supported federal and state agencies since
              2012 with budget operations, compliance and audit liaison, Working Capital
              Fund management, and financial-analysis services. Led by a principal with
              over 30 years of experience managing more than $1 billion in federal and
              state funds.
            </p>
            <div className="mt-12 flex flex-wrap gap-5">
              <Link to="/contact" data-testid="gov-contact-btn" className="btn-primary">
                Discuss a Requirement <ArrowRight size={16} />
              </Link>
              <a
                href="mailto:helliotassociates@gmail.com?subject=Capability%20Statement%20Request"
                data-testid="gov-cs-btn"
                className="btn-secondary"
              >
                <FileText size={16} /> Request Capability Statement
              </a>
              <a
                href="https://www.encyro.com/helliotassociates@gmail.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="gov-encyro-btn"
                className="btn-secondary"
              >
                <ShieldCheck size={16} /> Send Documents Securely
              </a>
            </div>
          </div>
        </div>
      </section>

      <div className="hairline max-w-7xl mx-auto" />

      {/* CORE SERVICES */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="text-xs uppercase tracking-[0.3em] text-[#8E6A2B] mb-5">Core Federal & Institutional Services</div>
        <h2 className="font-serif text-4xl md:text-5xl text-[#0F292F] font-light tracking-tight max-w-3xl leading-tight mb-16">
          Where we deliver value to public agencies.
        </h2>

        <div className="grid md:grid-cols-2 gap-px bg-black/10 border border-black/10">
          {SERVICES.map((s) => (
            <div
              key={s.title}
              data-testid={`gov-service-${s.title.toLowerCase().replace(/\s+/g, "-").replace(/[&()]/g, "")}`}
              className="bg-white p-10 md:p-12 hover:bg-[#F3F1EC] transition-colors duration-500"
            >
              <s.icon size={32} strokeWidth={1.2} className="text-[#8E6A2B] mb-8" />
              <h3 className="font-serif text-2xl md:text-3xl text-[#0F292F] mb-4 leading-tight">{s.title}</h3>
              <p className="text-[#52525B] leading-relaxed">{s.blurb}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PAST PERFORMANCE */}
      <section className="bg-[#0F292F] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="text-xs uppercase tracking-[0.3em] text-[#CFA968] mb-5">— Past Performance</div>
          <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight max-w-3xl leading-tight mb-16">
            Selected engagements.
          </h2>

          <div className="grid md:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {PAST_PERFORMANCE.map((p, i) => (
              <article
                key={p.client}
                data-testid={`gov-past-${i}`}
                className="bg-[#0F292F] p-8 md:p-10 hover:bg-[#173B43] transition-colors"
              >
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#CFA968] mb-3">{p.role}</div>
                <h3 className="font-serif text-2xl text-white mb-3 leading-tight">{p.client}</h3>
                <p className="text-white/70 text-sm leading-relaxed">{p.detail}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* DIFFERENTIATORS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="text-xs uppercase tracking-[0.3em] text-[#8E6A2B] mb-5">— Key Differentiators</div>
        <h2 className="font-serif text-4xl md:text-5xl text-[#0F292F] font-light tracking-tight max-w-3xl leading-tight mb-16">
          Why agencies choose to work with us.
        </h2>

        <div className="grid md:grid-cols-3 gap-px bg-black/10 border border-black/10">
          {DIFFERENTIATORS.map((d) => (
            <div key={d.title} className="bg-white p-10">
              <d.icon size={32} strokeWidth={1.2} className="text-[#8E6A2B] mb-8" />
              <h3 className="font-serif text-2xl text-[#0F292F] mb-4 leading-tight">{d.title}</h3>
              <p className="text-[#52525B] text-sm leading-relaxed">{d.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* COMPANY DATA */}
      <section className="bg-[#F3F1EC] border-t border-black/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="text-xs uppercase tracking-[0.3em] text-[#8E6A2B] mb-5">— Company Data</div>
              <h2 className="font-serif text-4xl md:text-5xl text-[#0F292F] font-light tracking-tight leading-tight">
                Quick reference for COs and contracting staff.
              </h2>
            </div>

            <div className="lg:col-span-8 space-y-12">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#8E6A2B] mb-4">NAICS Codes</div>
                <ul className="divide-y divide-black/10">
                  {NAICS.map((n) => (
                    <li key={n.code} className="py-4 flex items-baseline gap-6">
                      <span className="font-serif text-2xl text-[#0F292F] w-24 flex-shrink-0">{n.code}</span>
                      <span className="text-[#52525B]">{n.desc}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#8E6A2B] mb-4">Registrations & Certifications</div>
                <ul className="space-y-3 text-[#1A1A1A]">
                  <li className="flex items-start gap-4">
                    <span className="w-6 h-px bg-[#8E6A2B] mt-3 flex-shrink-0" />
                    <span><strong>SAM.gov:</strong> Active registration</span>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="w-6 h-px bg-[#8E6A2B] mt-3 flex-shrink-0" />
                    <span><strong>UEI:</strong> QLQ9DHP73RF2</span>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="w-6 h-px bg-[#8E6A2B] mt-3 flex-shrink-0" />
                    <span><strong>MBE — State of Maryland:</strong> Certified Minority Business Enterprise (MDOT)</span>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="w-6 h-px bg-[#8E6A2B] mt-3 flex-shrink-0" />
                    <span><strong>LSBRP:</strong> Certified under the Local Small Business Reserve Program (Montgomery County, MD)</span>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="w-6 h-px bg-[#8E6A2B] mt-3 flex-shrink-0" />
                    <span><strong>Founded:</strong> 2012 · Rockville, Maryland</span>
                  </li>
                </ul>
              </div>

              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#8E6A2B] mb-4">Affiliations</div>
                <ul className="space-y-3 text-[#1A1A1A]">
                  <li className="flex items-start gap-4">
                    <span className="w-6 h-px bg-[#8E6A2B] mt-3 flex-shrink-0" />
                    <span>Association of Government Accountants (AGA)</span>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="w-6 h-px bg-[#8E6A2B] mt-3 flex-shrink-0" />
                    <span>Financial Planning Association (FPA)</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-black/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <h2 className="font-serif text-4xl md:text-5xl text-[#0F292F] font-light tracking-tight">
              Have a federal or state requirement?
            </h2>
            <p className="mt-4 text-lg text-[#52525B] max-w-2xl">
              Send a brief description of the work, the period of performance, and the
              vehicle (set-aside, GSA, BPA, IDIQ, etc.). We'll respond within one
              business day with a fit assessment.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/contact" data-testid="gov-cta-contact" className="btn-primary">
              Start the Conversation <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
